/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  createContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import {
  DismissButton,
  mergeProps,
  Overlay,
  usePopover,
  type AriaPopoverProps,
} from "react-aria";
import type { OverlayTriggerState } from "react-stately";
import { cssFns } from "@superweb/css";
import { usePopoverContainer } from "../../../popover";

import type { ItemSize } from "../types";
import { useOverlayPosition } from "../../../overlay";

type PopoverProps = Omit<AriaPopoverProps, "popoverRef"> & {
  children: ReactNode;
  state: OverlayTriggerState;
  itemSize: ItemSize;
  parentContainerRef?: RefObject<HTMLDivElement>;
};

export const PopoverMaxHeightContext = createContext<number | undefined>(
  undefined,
);

export const PopoverParentContainerContext = createContext<
  RefObject<HTMLDivElement>
>({ current: null });

export const Popover = ({
  children,
  state,
  itemSize,
  parentContainerRef,
  ...props
}: PopoverProps) => {
  const [width, setWidth] = useState(0);
  const popoverContainer = usePopoverContainer();
  const popoverChildContainerRef = useRef<HTMLDivElement>(null);

  const popoverRef = useRef<HTMLDivElement>(null);
  const { popoverProps, underlayProps } = usePopover(
    {
      ...props,
      popoverRef,
    },
    state,
  );

  const overlayProps = useOverlayPosition({
    ...props,
    overlayRef: popoverRef,
    targetRef: props.triggerRef,
  });

  const maxHeight = popoverProps.style?.maxHeight
    ? Number(popoverProps.style.maxHeight)
    : undefined;

  useEffect(() => {
    if (popoverChildContainerRef.current) {
      setWidth(popoverChildContainerRef.current.offsetWidth);
    }
  }, [popoverChildContainerRef]);

  return (
    <Overlay
      portalContainer={
        parentContainerRef?.current
          ? parentContainerRef.current
          : popoverContainer
      }
    >
      <div
        {...underlayProps}
        css={{ position: "fixed", ...cssFns.inset("0") }}
      />
      <div
        {...mergeProps(popoverProps, overlayProps)}
        ref={popoverRef}
        css={{
          width: "max-content",
          minWidth: `${width}px`,
        }}
      >
        <div ref={popoverChildContainerRef} />
        <PopoverParentContainerContext.Provider
          value={popoverChildContainerRef}
        >
          <PopoverMaxHeightContext.Provider value={maxHeight}>
            <DismissButton onDismiss={state.close} />
            {children}
            <DismissButton onDismiss={state.close} />
          </PopoverMaxHeightContext.Provider>
        </PopoverParentContainerContext.Provider>
      </div>
    </Overlay>
  );
};
